// The guided tours, one per menu entry. Texts: i18n/<lang>/tour.ts (steps.<tour>.<step>).
// Selectors target what the screens already render; a missing element shows the card centred instead.
// Kept up to date by two checks: test/tour.test.ts (every menu entry has a tour and a welcome step)
// and the e2e journey (every highlighted element still exists on its screen, see checkTour).
import type { TourDef } from './tour.js';
import { state } from '../../data/store.js';

/** Closes the open modal (workout sheet…) the way a click outside it would. */
const closeModals = () => document.querySelectorAll<HTMLElement>('.overlay').forEach(ov => ov.dispatchEvent(new MouseEvent('mousedown', { bubbles: true })));
const openItems = () => { const d = document.querySelector<HTMLDetailsElement>('details.fold'); if (d) d.open = true; };

export const TOURS: TourDef[] = [
  {
    id: 'welcome', icon: 'home', route: '/',
    steps: [
      { id: 'hello', route: '/' },
      { id: 'hero', target: '.card.hero', side: 'bottom' },
      { id: 'play', target: '[data-nav="play"]', side: 'right' },
      { id: 'training', target: '[data-nav="training"]', side: 'right' },
      { id: 'journal', target: '[data-nav="journal"]', side: 'right' },
      { id: 'rider', target: '[data-nav="wardrobe"]', side: 'right' },
      { id: 'store', target: '[data-nav="store"]', side: 'right' },
      { id: 'community', target: '[data-nav="community"]', side: 'right' },
      { id: 'creations', target: '[data-nav="workshop"]', side: 'right' },
      { id: 'settings', target: '[data-nav="settings"]', side: 'right' },
      { id: 'level', target: '#sb-lvl', side: 'right' },
      { id: 'devices', target: '.sb-foot .devs', side: 'right' },
      { id: 'more', target: '[data-nav="tutorials"]', side: 'right' },
    ],
  },
  {
    id: 'play', icon: 'flag', route: '/play',
    steps: [
      { id: 'tabs', route: '/play', target: '.hub-tabs', side: 'bottom' },
      { id: 'who', target: '.card:has([data-with])', side: 'right' },
      { id: 'code', target: '.play-code', side: 'bottom' },
      { id: 'modes', target: '.card:has([data-mode])', wait: 'click', side: 'right' },
      { id: 'tracks', target: '.card:has([data-track])', side: 'right' },
      { id: 'themes', target: '.card:has([data-theme])', side: 'right' },
      // only modes with items have this card
      { id: 'items', target: 'details.fold', before: openItems, side: 'right', optional: true },
      { id: 'options', target: '.play-side', side: 'left' },
      { id: 'start', target: '.play-side [data-act="go"]', side: 'left' },
      { id: 'arcade', target: '.card:has([data-game])', side: 'top' },
    ],
  },
  {
    id: 'challenges', icon: 'mountain', route: '/challenges',
    steps: [
      { id: 'intro', route: '/challenges', target: '.page-head', side: 'bottom' },
      { id: 'card', target: '.ch-card', side: 'right' },
      { id: 'clock', target: '.ch-card .inline.small', side: 'bottom' },
      { id: 'start', target: '.ch-card [data-start]', side: 'top' },
    ],
  },
  {
    id: 'training', icon: 'activity', route: '/training',
    steps: [
      { id: 'tabs', route: '/training', target: '.page-head .tabs', side: 'bottom' },
      { id: 'pick', target: '.wcard', wait: 'click', side: 'right' },
      { id: 'sheet', target: '.modal', side: 'left' },
      { id: 'erg', target: '.modal [data-a="go"]', side: 'top' },
      { id: 'plans', route: '/training/plans', before: closeModals, target: '.page > .grid.g2, .page .grid.g2', side: 'top' },
      { id: 'create', route: '/training/create', target: '.page .grid.g-main', side: 'top' },
    ],
  },
  {
    id: 'workshop', icon: 'sparkles', route: '/workshop',
    steps: [
      { id: 'intro', route: '/workshop', target: '.workshop .page-head', side: 'bottom' },
      { id: 'kinds', target: '.workshop .chips.kinds', side: 'bottom' },
      { id: 'ask', target: '#ask', side: 'bottom' },
      // the example links only show while no creation is open
      { id: 'example', target: '[data-example="workout"]', wait: 'click', side: 'bottom', optional: true },
      { id: 'preview', target: '.workshop .g-main > .card:nth-child(2)', side: 'left' },
      { id: 'try', target: '.workshop [data-act="try"]', side: 'top' },
      { id: 'publish', target: '.workshop [data-act="publish"]', side: 'top' },
    ],
  },
  {
    id: 'store', icon: 'library', route: '/store',
    steps: [
      { id: 'store', route: '/store', target: '.page .tabs.wrap', side: 'bottom' },
      { id: 'collection', target: '.chips.sources', side: 'bottom' },
    ],
  },
  {
    id: 'rider', icon: 'shirt', route: '/wardrobe',
    steps: [
      { id: 'preview', route: '/wardrobe', target: '#rp', side: 'right' },
      { id: 'body', target: '#panel', side: 'left' },
      { id: 'slots', target: '#panel .tabs', side: 'bottom' },
      { id: 'team', target: '#team', side: 'top' },
      { id: 'store', target: '.page-head [data-store-link]', side: 'bottom' },
    ],
  },
  {
    id: 'events', icon: 'calendar', route: '/events',
    steps: [
      { id: 'intro', route: '/events', target: '.page-head', side: 'bottom' },
      { id: 'calendar', target: '#ev-body', side: 'top' },
      { id: 'tabs', target: '.page-head .tabs', side: 'bottom' },
      { id: 'create', target: '.page-head .tabs [data-tab="create"]', side: 'bottom' },
    ],
  },
  {
    id: 'journal', icon: 'chart', route: '/activities',
    steps: [
      { id: 'activities', route: '/activities', target: '.page', side: 'bottom', pad: 0 },
      { id: 'replay', target: '.page', side: 'bottom', pad: 0 },
      { id: 'progress', route: '/progress', target: '.card.hero', side: 'bottom' },
      { id: 'unlocks', target: '.card:has(.unlocks)', side: 'top' },
    ],
  },
  {
    id: 'community', icon: 'users', route: '/leaderboards',
    steps: [
      { id: 'leaderboards', route: '/leaderboards', target: '.page-head', side: 'bottom' },
      { id: 'friends', route: '/friends', target: '.page-head', side: 'bottom' },
      { id: 'club', route: '/club', target: '.page-head', side: 'bottom' },
    ],
  },
  {
    id: 'settings', icon: 'settings', route: '/settings',
    steps: [
      { id: 'tabs', route: '/settings', target: '.page-head .tabs', side: 'bottom' },
      { id: 'account', target: '[data-acc]', side: 'bottom' },
      { id: 'profile', target: '.card:has([data-ftp])', side: 'right' },
      { id: 'privacy', target: '[data-legal]', side: 'left' },
      { id: 'devices', route: '/settings/devices', target: '[data-setup-hero]', side: 'bottom' },
      { id: 'steer', target: '[data-ctl-hub]', side: 'top' },
      { id: 'controls', route: '/settings/controls', target: '.card:has([data-key-assign])', side: 'top' },
      { id: 'game', route: '/settings/game', target: '.card:has([data-s="tdiff"])', side: 'right' },
      { id: 'graphics', target: '.card:has([data-s="gfx"])', side: 'left' },
      { id: 'language', route: '/settings/general', target: '[data-culture]', side: 'bottom' },
    ],
  },
];

export const tourById = (id: string) => TOURS.find(x => x.id === id);
/** Tour offered on the first visit of a screen (navigation id of the route → tour). */
export const TOUR_FOR_NAV: Record<string, string> = {
  home: 'welcome', play: 'play', challenges: 'challenges', training: 'training', workshop: 'workshop', store: 'store', wardrobe: 'rider',
  events: 'events', activities: 'journal', progress: 'journal', friends: 'community', club: 'community', leaderboards: 'community', settings: 'settings',
};
export const toursState = () => state.tours as { done: string[]; offered: string[] };
